import { useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import useFormularioTetrazolio from '../../hooks/useFormularioTetrazolio';
import FormularioTetrazolioHeader from './FormularioTetrazolioHeader/FormularioTetrazolioHeader';
import FormularioTetrazolioAnaliseInicial from './FormularioTetrazolioAnaliseInicial/FormularioTetrazolioAnaliseInicial';
import FormularioTetrazolioClassificacao from './FormularioTetrazolioClassificacao/FormularioTetrazolioClassificacao';
import Header from '../../../../../components/Header/Header';
import PageTitle from '../../../../../components/PageTitle/PageTitle';

type AnaliseInicialFormValues = {
  analistaId: string;
  data: Date | undefined;
  solucao: string;
  repeticoes: 2 | 4;
  sementesPorRepeticao: 50 | 100;
  observacao?: string;
  gerarBASRIR?: boolean;
};

type AnaliseInicialSubmitValues = AnaliseInicialFormValues & {
  gerarBASRIR: boolean;
  observacao: string;
};

export default function FormularioTetrazolioPage() {
  const { id } = useParams<{ id: string }>();
  const {
    detalhes, loading, error, analiseInicial, classificacao, configuracoes, imagens, refetch,
  } = useFormularioTetrazolio(id || '');

  // Só mostra classificação se:
  // - NÃO foi cancelado E
  // - Submeteu o formulário nesta sessão (analiseInicial.dadosAnaliseInicial existe)
  const dadosAnaliseInicialFinal = (
    analiseInicial.formularioCancelado || !analiseInicial.dadosAnaliseInicial)
    ? undefined
    : analiseInicial.dadosAnaliseInicial;

  // Tem que fazer essa conversão de tipos por que a api pode devolver as repetições de
  // forma diferente, mas o resto do código trata para que funcione corretamente
  const handleSubmitAnaliseInicial = useCallback((values: AnaliseInicialSubmitValues) => {
    // Se a análise já está realizada, não precisa submeter para API, só mostrar classificação
    if (detalhes?.status === 'Realizado') {
      analiseInicial.confirmarDadosExistentes(values);
      return;
    }

    analiseInicial.submitAnaliseInicial(
      values,
      id || '',
    );
  }, [analiseInicial, id, detalhes?.status]);

  const analiseInicialValues: AnaliseInicialFormValues | undefined = useMemo(() => {
    if (!detalhes?.dadosAnaliseInicial) return undefined;

    const repeticoes = detalhes.dadosAnaliseInicial.repeticoes < 4 ? 2 : 4;

    return {
      analistaId: detalhes.dadosAnaliseInicial.analistaId,
      data: detalhes.dadosAnaliseInicial.data,
      solucao: detalhes.dadosAnaliseInicial.solucao,
      repeticoes,
      sementesPorRepeticao: detalhes.dadosAnaliseInicial.sementesPorRepeticao as 50 | 100,
      observacao: detalhes.dadosAnaliseInicial.observacao,
      gerarBASRIR: detalhes.dadosAnaliseInicial.gerarBASRIR,
    };
  }, [detalhes?.dadosAnaliseInicial]);

  return (
    <div className="lista-sementes-page">
      <Header lockProdutor={!!dadosAnaliseInicialFinal}>
        <PageTitle breadcrumbs={[{
          label: 'Laboratório de Sementes', url: '/sementes',
        }, { label: 'Análise' }]}
        />
      </Header>

      {loading && <div>Carregando...</div>}
      {error && <div>Erro: {error}</div>}
      {!loading && !error && !detalhes && <div>Análise não encontrada</div>}

      {!loading && !error && detalhes && (
        <>
          <FormularioTetrazolioHeader
            produtor={detalhes.produtor}
            idDaAmostra={detalhes.idDaAmostra}
            numeroRemessa={detalhes.numeroRemessa}
            especie={detalhes.especie}
            lote={detalhes.lote}
            safra={detalhes.safra}
            nomeCientifico={detalhes.nomeCientifico}
            cultivar={detalhes.cultivar}
            categoria={detalhes.categoria}
            dataEntrada={detalhes.dataEntrada}
            dataAmostragem={detalhes.dataAmostragem}
            status={detalhes.status}
            concluidoApi={detalhes.concluidoApi}
            dadosAnaliseInicial={dadosAnaliseInicialFinal}
            onCancelarAnalise={async () => {
              classificacao.resetarClassificacao();
              analiseInicial.cancelarAnaliseInicial();
              await refetch();
            }}
            imagens={imagens.imagens}
            onAdicionarImagem={imagens.adicionarImagem}
            onRemoverImagem={imagens.removerImagem}
            loadingImagens={imagens.loading}
            analiseId={id || ''}
          />

          {!dadosAnaliseInicialFinal && (
            <FormularioTetrazolioAnaliseInicial
              analistas={analiseInicial.analistas}
              searchAnalistas={analiseInicial.searchAnalistas}
              onSubmit={handleSubmitAnaliseInicial}
              loading={analiseInicial.loading}
              initialValues={analiseInicialValues}
              submitButtonText={detalhes?.dadosAnaliseInicial ? 'Continuar Análise' : undefined}
              bloqueiaRepeticoes={!!detalhes?.dadosAnaliseInicial}
              status={detalhes.status}
            />
          )}

          {dadosAnaliseInicialFinal && (
            <FormularioTetrazolioClassificacao
              classificacao={classificacao}
              configuracoes={configuracoes}
              analiseId={id || ''}
              status={detalhes.status}
              idDaAmostra={detalhes.idDaAmostra}
              concluidoApi={detalhes.concluidoApi}
              onConcluir={refetch}
            />
          )}
        </>
      )}
    </div>
  );
}
